import { Link } from 'react-router-dom';
import { PencilIcon } from '@heroicons/react/24/outline';
import type { ReactNode, TdHTMLAttributes } from 'react';
import { DataTable, DataTableBody, DataTableHead, TableHeader } from '../../ui/DataTable';
import type { ModelReviewEntry, ModelReviewField, ModelStatus } from '../../../types';
import {
  LIMIT_FIELDS,
  SOURCE_LABELS,
  actionableDiffers,
  formatLimit,
  formatPrice,
  providerValueFor,
} from './reviewUtils';

/**
 * "In the catalog" (task 1093): one row per Model row, every status including
 * retired (read from the review document, because `GET /models` hides retired
 * Models). Each comparison cell shows the stored value over the provider
 * value; a differing cell is highlighted and, for a User allowed to write,
 * carries "Use N", which writes the provider value onto the Model row. A
 * field the feed has no value for reads "provider has no value" and has no
 * Use action (task 1277).
 */

/** A body cell with the table's padding; unlike `DataTableCell` it passes data attributes through. */
function Td({ children, className = '', ...rest }: TdHTMLAttributes<HTMLTableCellElement> & { children?: ReactNode }) {
  return (
    <td className={`px-3 py-3 ${className}`} {...rest}>
      {children}
    </td>
  );
}

const SMALL_BUTTON =
  'inline-flex items-center rounded border px-1.5 py-0.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50';
const USE_BUTTON = `${SMALL_BUTTON} border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:bg-gray-800 dark:text-indigo-300 dark:hover:bg-gray-700`;
const MARK_BUTTON = `${SMALL_BUTTON} border-amber-400 bg-white text-amber-800 hover:bg-amber-50 dark:border-amber-600 dark:bg-gray-800 dark:text-amber-300 dark:hover:bg-gray-700`;

const STORED_STATUS_BADGE: Record<ModelStatus, string> = {
  active: '',
  deprecated: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  retired: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

interface ReviewCatalogTableProps {
  entries: ModelReviewEntry[];
  modelsDevUnavailable: boolean;
  canWrite: boolean;
  busyId: string | null;
  onUse: (entry: ModelReviewEntry, fields: ModelReviewField[]) => void;
  onMark: (entry: ModelReviewEntry, status: Extract<ModelStatus, 'deprecated' | 'retired'>) => void;
}

const hasTiers = (tiers: unknown) => Array.isArray(tiers) && tiers.length > 0;

function reviewStateLabel(entry: ModelReviewEntry): string {
  const provider = SOURCE_LABELS[entry.provider] ?? entry.provider;
  switch (entry.reviewState) {
    case 'listed':
    case 'listed_alias':
      return 'Listed';
    case 'deprecated':
      return entry.shutdownDate ? `Shuts down ${entry.shutdownDate}` : 'Deprecated';
    case 'unknown':
      return `Unknown · not in ${provider}'s list`;
    case 'unverified':
      return `Unverified · ${provider} list unavailable`;
  }
}

const REVIEW_STATE_CHIP: Record<ModelReviewEntry['reviewState'], string> = {
  listed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  listed_alias: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  deprecated: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  unknown: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  unverified: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function StatusCell({
  entry,
  canWrite,
  busy,
  onMark,
}: {
  entry: ModelReviewEntry;
  canWrite: boolean;
  busy: boolean;
  onMark: ReviewCatalogTableProps['onMark'];
}) {
  // Task 1130: `successor` is the stored successor's model id when the row has
  // one (service task 1129), else the computed in-family hint.
  const hasStoredSuccessor = entry.successorModelId != null;
  const markDeprecated = entry.reviewState === 'deprecated' && entry.status === 'active';
  const markRetired = entry.reviewState === 'unknown' && entry.status !== 'retired';
  return (
    <div className="flex flex-col items-start gap-1">
      <span
        data-testid={`review-state-${entry.modelId}`}
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${REVIEW_STATE_CHIP[entry.reviewState]}`}
      >
        {reviewStateLabel(entry)}
      </span>
      {entry.status !== 'active' && (
        <span
          data-testid={`review-stored-status-${entry.modelId}`}
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STORED_STATUS_BADGE[entry.status]}`}
        >
          {entry.status === 'retired' ? 'Retired' : 'Marked deprecated'}
        </span>
      )}
      {entry.shutdownDate && entry.reviewState !== 'deprecated' && (
        <span className="text-xs text-gray-500 dark:text-gray-400">Shuts down {entry.shutdownDate}</span>
      )}
      {entry.successor && hasStoredSuccessor && (
        <span data-testid={`review-successor-${entry.modelId}`} className="text-xs text-gray-500 dark:text-gray-400">
          falls back to <span className="font-mono">{entry.successor}</span>
        </span>
      )}
      {entry.successor && !hasStoredSuccessor && (
        <span data-testid={`review-successor-${entry.modelId}`} className="text-xs text-gray-500 dark:text-gray-400">
          successor in family: <span className="font-mono">{entry.successor}</span>
        </span>
      )}
      {entry.status === 'deprecated' && !entry.successor && (
        <span data-testid={`review-no-fallback-${entry.modelId}`} className="text-xs text-amber-700 dark:text-amber-300">
          no fallback
        </span>
      )}
      {canWrite && markDeprecated && (
        <button type="button" disabled={busy} onClick={() => onMark(entry, 'deprecated')} className={MARK_BUTTON}>
          Mark deprecated
        </button>
      )}
      {canWrite && markRetired && (
        <button type="button" disabled={busy} onClick={() => onMark(entry, 'retired')} className={MARK_BUTTON}>
          Mark retired
        </button>
      )}
    </div>
  );
}

function CompareCell({
  entry,
  field,
  actionable,
  canWrite,
  busy,
  onUse,
}: {
  entry: ModelReviewEntry;
  field: ModelReviewField;
  actionable: ModelReviewField[];
  canWrite: boolean;
  busy: boolean;
  onUse: ReviewCatalogTableProps['onUse'];
}) {
  const isLimit = LIMIT_FIELDS.has(field);
  const stored = entry.stored[field] as number | string | null;
  // undefined: the feed has no value for this field, so there is nothing to apply (task 1277).
  const provider = providerValueFor(entry, field) as number | string | undefined;
  const format = (value: number | string | null | undefined) => (isLimit ? formatLimit(value as number | null) : formatPrice(value));
  const differs = entry.differs.includes(field);
  const isPriceCell = field === 'inputPriceMTok' || field === 'outputPriceMTok';
  const tiersShown = isPriceCell && (hasTiers(entry.stored.pricingTiers) || hasTiers(entry.pricing?.tiers));
  const tiersDiffer = entry.differs.includes('pricingTiers');
  const tiersMissing = field === 'inputPriceMTok' && tiersDiffer && !hasTiers(entry.pricing?.tiers);
  // An OpenAI row's limits come from models.dev: advisory, so the source is named.
  const advisorySource = isLimit && entry.provider_values.source === 'models-dev' ? SOURCE_LABELS['models-dev'] : null;

  return (
    <Td
      data-testid={`review-cell-${entry.modelId}-${field}`}
      data-differs={differs ? 'true' : 'false'}
      className={`align-top text-sm ${differs ? 'bg-amber-50 dark:bg-amber-950/30' : ''}`}
    >
      <div className="flex flex-col items-start gap-0.5 whitespace-nowrap">
        <span className="text-gray-900 dark:text-white">
          {format(stored)}
          {tiersShown && (
            <span
              className={`ml-1 rounded px-1 text-[10px] uppercase ${
                tiersDiffer
                  ? 'bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}
            >
              tiers
            </span>
          )}
        </span>
        <span className={provider === undefined ? 'text-xs italic text-gray-400 dark:text-gray-500' : 'text-xs text-gray-500 dark:text-gray-400'}>
          {provider === undefined ? 'provider has no value' : format(provider)}
          {provider !== undefined && advisorySource && <span className="ml-1 text-gray-400 dark:text-gray-500">· {advisorySource}</span>}
        </span>
        {tiersMissing && (
          <span data-testid={`review-tiers-${entry.modelId}`} className="text-xs italic text-gray-400 dark:text-gray-500">
            Pricing tiers: provider has no value
          </span>
        )}
        {canWrite && differs && provider !== undefined && actionable.includes(field) && (
          <button type="button" disabled={busy} onClick={() => onUse(entry, [field])} className={USE_BUTTON}>
            Use {format(provider)}
          </button>
        )}
        {canWrite && field === 'inputPriceMTok' && actionable.includes('pricingTiers') && (
          <button type="button" disabled={busy} onClick={() => onUse(entry, ['pricingTiers'])} className={USE_BUTTON}>
            Use tiers
          </button>
        )}
      </div>
    </Td>
  );
}

const COMPARED: ModelReviewField[] = [
  'contextWindow',
  'maxOutputTokens',
  'inputPriceMTok',
  'outputPriceMTok',
  'cacheReadPriceMTok',
  'cacheWritePriceMTok',
];

export function ReviewCatalogTable({
  entries,
  modelsDevUnavailable,
  canWrite,
  busyId,
  onUse,
  onMark,
}: ReviewCatalogTableProps) {
  if (entries.length === 0) {
    return <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">No Models match this filter.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <DataTable>
        <DataTableHead>
          <TableHeader>Model</TableHeader>
          <TableHeader>Status</TableHeader>
          <TableHeader>Configs</TableHeader>
          <TableHeader>Agents</TableHeader>
          <TableHeader>Context window</TableHeader>
          <TableHeader>Max output</TableHeader>
          <TableHeader>Input $/MTok</TableHeader>
          <TableHeader>Output $/MTok</TableHeader>
          <TableHeader>Cache read</TableHeader>
          <TableHeader>Cache write</TableHeader>
          <TableHeader>
            <span className="sr-only">Actions</span>
          </TableHeader>
        </DataTableHead>
        <DataTableBody>
          {entries.map((entry) => {
            const actionable = actionableDiffers(entry, modelsDevUnavailable);
            const busy = busyId === entry.id;
            return (
              <tr key={entry.id} data-testid={`review-row-${entry.modelId}`}>
                <Td className="align-top">
                  <div className="whitespace-nowrap font-mono text-sm text-gray-900 dark:text-white">{entry.modelId}</div>
                  {entry.resolvedModelId && entry.resolvedModelId !== entry.modelId && (
                    <div className="whitespace-nowrap font-mono text-xs text-gray-500 dark:text-gray-400">→ {entry.resolvedModelId}</div>
                  )}
                  <div className="text-xs text-gray-500 dark:text-gray-400">{entry.provider}</div>
                </Td>
                <Td className="align-top">
                  <StatusCell entry={entry} canWrite={canWrite} busy={busy} onMark={onMark} />
                </Td>
                <Td className="align-top text-sm">
                  <Link
                    to={`/model-catalog?model=${encodeURIComponent(entry.id)}`}
                    className="text-indigo-600 hover:underline dark:text-indigo-400"
                    aria-label={`${entry.configCount} Model Configs on ${entry.modelId}`}
                  >
                    {entry.configCount}
                  </Link>
                </Td>
                <Td className="align-top text-sm text-gray-700 dark:text-gray-300">{entry.agentCount}</Td>
                {COMPARED.map((field) => (
                  <CompareCell
                    key={field}
                    entry={entry}
                    field={field}
                    actionable={actionable}
                    canWrite={canWrite}
                    busy={busy}
                    onUse={onUse}
                  />
                ))}
                <Td className="align-top">
                  {canWrite && (
                    <div className="flex flex-col items-end gap-1">
                      {actionable.length > 0 && (
                        <button type="button" disabled={busy} onClick={() => onUse(entry, actionable)} className={USE_BUTTON}>
                          Use all ({actionable.length})
                        </button>
                      )}
                      <Link
                        to={`/model-catalog/models/${entry.id}?edit=1`}
                        aria-label={`Edit ${entry.modelId}`}
                        className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                        Edit
                      </Link>
                    </div>
                  )}
                </Td>
              </tr>
            );
          })}
        </DataTableBody>
      </DataTable>
    </div>
  );
}
